<template>
  <v-container class="d-flex flex-column ga-3">
    <UsageExample v-model="tab" :code="code" :tabs="tabs">
      <v-row>
        <v-col>
          <v-card elevation="4" class="regular-card">
            <div class="font-weight-bold px-2 pb-4">Regular</div>
            <cs-header-container
              label="Example Elementary School"
              variant="regular"
              :chip-props="{ color: 'teal', label: 'Parent site' }"
              :items="items"
              :show-settings-button="hideSettingsBtn ? false : true"
              :show-updates-button="hideUpdatesBtn ? false : true"
              :show-feedback-button="hideFeedbackBtn ? false : true"
              :school-logo="hideEmblem ? '' : imgUrl"
              @update:option-select-value="handleSelection"
              @manage-settings="handleManageSettings"
              @check-updates="handleCheckUpdates"
              @give-feedback="handleGiveFeedback"
              @school-logo-click="handleSchoolLogoClick"
            />
          </v-card>

          <v-card elevation="4" class="regular-nested-card">
            <div class="font-weight-bold px-2 pb-4">Regular with Nested Items</div>
            <cs-header-container
              label="Example Elementary School"
              variant="regular"
              :chip-props="{ color: 'teal', label: 'Parent site' }"
              :items="nestedItems"
              :is-nested="true"
              :show-settings-button="hideSettingsBtn ? false : true"
              :show-updates-button="hideUpdatesBtn ? false : true"
              :show-feedback-button="hideFeedbackBtn ? false : true"
              :school-logo="hideEmblem ? '' : imgUrl"
              @update:option-select-value="handleSelection"
              @manage-settings="handleManageSettings"
              @check-updates="handleCheckUpdates"
              @give-feedback="handleGiveFeedback"
              @school-logo-click="handleSchoolLogoClick"
            />
          </v-card>

          <v-card elevation="4" class="regular-card">
            <div class="font-weight-bold px-2 pb-4">Regular with Custom Actions</div>
            <cs-header-container
              label="Example Elementary School"
              variant="regular"
              :chip-props="{ color: 'teal', label: 'Parent site' }"
              :items="items"
              :school-logo="hideEmblem ? '' : imgUrl"
              @school-logo-click="handleSchoolLogoClick"
              @update:option-select-value="handleSelection"
            >
              <template #actions>
                <hx-button
                  id="create-record-button"
                  label="Create"
                  left-icon="$i-add-record"
                  variant="primary"
                  :disabled="!draftMode"
                  @click="handleCreateRecord"
                />
                <div class="draft-mode-container">
                  <v-icon icon="$i-file-edit" class="draft-icon" />
                  <span class="draft-mode-label">Draft</span>
                  <hx-toggle-switch id="draft-mode" v-model="draftMode" />
                </div>
                <div class="admin-settings-container">
                  <v-icon icon="mdi-account-cog" class="admin-settings-icon" />
                  <span class="admin-settings-label">Admin</span>
                  <hx-toggle-switch id="admin-settings" v-model="adminSettings" />
                </div>
                <hx-button
                  id="settings-button"
                  label="Settings"
                  left-icon="mdi-cog"
                  @click="handleManageSettings"
                />
                <hx-button
                  id="search-button"
                  label="Search"
                  left-icon="mdi-magnify"
                  @click="handleSearch"
                />
              </template>
            </cs-header-container>
          </v-card>

          <v-card elevation="4" class="regular-nested-card">
            <div class="font-weight-bold px-2 pb-4">Lite</div>
            <cs-header-container
              label="Example Elementary School"
              variant="lite"
              :icon="'$i-shield'"
              :show-chip="false"
              @header-click="handleHeaderClick"
              @school-logo-click="handleSchoolLogoClick"
            />
          </v-card>

          <v-card elevation="4" class="regular-nested-card">
            <div class="font-weight-bold px-2 pb-4">Lite</div>
            <cs-header-container
              label="Example Elementary School"
              variant="lite"
              :school-logo="imgUrl"
              :show-chip="true"
              :chip-props="{ color: 'pink', label: 'Child site' }"
              @header-click="handleHeaderClick"
              @school-logo-click="handleSchoolLogoClick"
            >
              <template #actions>
                <hx-button
                  id="create-record-button"
                  label="Create"
                  left-icon="$i-add-record"
                  variant="primary"
                  :disabled="!draftMode"
                  @click="handleCreateRecord"
                />
                <div class="draft-mode-container">
                  <v-icon icon="$i-file-edit" class="draft-icon" />
                  <span class="draft-mode-label">Draft</span>
                  <hx-toggle-switch id="draft-mode" v-model="draftMode" />
                </div>
                <div class="admin-settings-container">
                  <v-icon icon="mdi-account-cog" class="admin-settings-icon" />
                  <span class="admin-settings-label">Admin</span>
                  <hx-toggle-switch id="admin-settings" v-model="adminSettings" />
                </div>
                <hx-button
                  id="settings-button"
                  label="Settings"
                  left-icon="mdi-cog"
                  @click="handleManageSettings"
                />
                <hx-button
                  id="search-button"
                  label="Search"
                  left-icon="mdi-magnify"
                  @click="handleSearch"
                />
              </template>
            </cs-header-container>
          </v-card>
        </v-col>
      </v-row>
    </UsageExample>
  </v-container>
</template>

<script>
import UsageExample from '@/components/helix/utils/UsageExample';

export default {
  name: 'CSHeaderContainer',
  components: { UsageExample },

  setup() {
    const code = `
<cs-header-container
      label="Example Elementary School"
      variant="regular"
      :chip-props="{ color: 'teal', label: 'Parent site' }"
      :items="items"
      :is-nested="false"
      :show-settings-button="false"
      :show-updates-button="false"
      :show-feedback-button="false"
      :school-logo="imgUrl"
      @update:option-select-value="handleSelection"
      @manage-settings="handleManageSettings"
      @check-updates="handleCheckUpdates"
      @give-feedback="handleGiveFeedback"
  />

<cs-header-container
      label="Example Elementary School"
      variant="lite"
      :icon="'$i-shield'"
      :show-chip="false"
      @header-click="handleHeaderClick"
      @school-logo-click="handleSchoolLogoClick"
  />

<cs-header-container
      label="Example Elementary School"
      variant="regular"
      :chip-props="{ color: 'teal', label: 'Parent site' }"
      :items="items"
      :school-logo="hideEmblem ? '' : imgUrl"
      @update:option-select-value="handleSelection"
    >
      <template #actions>
        <hx-button
                          id="create-record-button"
                          label="Create"
                          left-icon="$i-add-record"
                          variant="primary"
                          :disabled="!draftMode"
                          @click="handleCreateRecord"
                        />
        <div class="draft-mode-container">
          <v-icon icon="$i-file-edit" class="draft-icon" />
          <span class="draft-mode-label">Draft</span>
          <hx-toggle-switch id="draft-mode" v-model="draftMode" />
        </div>
        <div class="admin-settings-container">
          <v-icon icon="mdi-account-cog" class="admin-settings-icon" />
          <span class="admin-settings-label">Admin</span>
          <hx-toggle-switch id="admin-settings" v-model="adminSettings" />
        </div>
        <hx-button
                          id="settings-button"
                          label="Settings"
                          left-icon="mdi-cog"
                          @click="handleManageSettings"
                        />
        <hx-button
                          id="search-button"
                          label="Search"
                          left-icon="mdi-magnify"
                          @click="handleSearch"
                        />
      </template>
    </cs-header-container>
<cs-header-container
      label="Example Elementary School"
      variant="lite"
      :school-logo="imgUrl"
      :show-chip="true"
      :chip-props="{ color: 'pink', label: 'Child site' }"
      @header-click="handleHeaderClick"
      @school-logo-click="handleSchoolLogoClick"
    >
      <template #actions>
        <hx-button
          id="create-record-button"
          label="Create"
          left-icon="$i-add-record"
          variant="primary"
          :disabled="!draftMode"
          @click="handleCreateRecord"
        />
        <div class="draft-mode-container">
          <v-icon icon="$i-file-edit" class="draft-icon" />
          <span class="draft-mode-label">Draft</span>
          <hx-toggle-switch id="draft-mode" v-model="draftMode" />
        </div>
        <div class="admin-settings-container">
          <v-icon icon="mdi-account-cog" class="admin-settings-icon" />
          <span class="admin-settings-label">Admin</span>
          <hx-toggle-switch id="admin-settings" v-model="adminSettings" />
        </div>
        <hx-button
          id="settings-button"
          label="Settings"
          left-icon="mdi-cog"
          @click="handleManageSettings"
        />
        <hx-button
          id="search-button"
          label="Search"
          left-icon="mdi-magnify"
          @click="handleSearch"
        />
      </template>
    </cs-header-container>
    `;

    return {
      code
    };
  },

  data() {
    return {
      tab: 'Header Container Usage',
      tabs: ['Header Container Usage'],

      items: [
        { title: 'Example Site Name' },
        { title: 'Example Site Name' },
        { title: 'Example Site Name' },
        { title: 'Example Site Name' },
        {
          title: 'Example Site Name'
        },
        { title: 'Example Site Name' },
        { title: 'Example Site Name' },
        { title: 'Example Site Name' }
      ],
      nestedItems: [
        {
          title: 'Example Site Name',
          children: [
            { title: 'Example Site Name' },
            {
              title: 'Example Site Name'
            }
          ]
        },
        { title: 'Example Site Name' },
        { title: 'Example Site Name' },
        { title: 'Example Site Name' },
        { title: 'Example Site Name' },
        { title: 'Example Site Name' },
        { title: 'Example Site Name' },
        { title: 'Example Site Name' },
        { title: 'Example Site Name' },
        { title: 'Example Site Name' }
      ],
      imgUrl:
        'https://s3-us-west-2.amazonaws.com/develop.contentengine.compliengine.com/client-assets/212/db2d3267f4d3c75aaedbd8351433c04e-small.png',
      hideEmblem: false,
      hideSettingsBtn: false,
      hideUpdatesBtn: false,
      hideFeedbackBtn: false,
      draftMode: false,
      adminSettings: false
    };
  },

  methods: {
    handleSelection(value) {
      console.log('Switched header container value to: ', value);
    },
    handleManageSettings() {
      console.log('Manage settings button clicked');
    },
    handleCheckUpdates() {
      console.log('Check updates button clicked');
    },
    handleGiveFeedback() {
      console.log('Give feedback button clicked');
    },
    handleHeaderClick() {
      console.log('Header clicked');
    },
    handleSchoolLogoClick() {
      console.log('School logo clicked');
    },
    handleCreateRecord() {
      console.log('Create record button clicked');
    },
    handleSearch() {
      console.log('Search button clicked');
    }
  }
};
</script>

<style scoped>
.regular-card,
.regular-nested-card {
  height: 120px;
  overflow: visible;
  transition: height 0.3s ease;

  margin-bottom: 20px;
  padding: 16px;
}

.regular-card:has(.cs-option-select__dropdown[style*='display: block']),
.regular-card:has(.cs-option-select__dropdown:not([style*='display: none'])) {
  height: 350px;
}

.regular-nested-card:has(.cs-option-select__dropdown[style*='display: block']),
.regular-nested-card:has(.cs-option-select__dropdown:not([style*='display: none'])) {
  height: 430px;
}

.draft-mode-container {
  display: flex;
  align-items: center;
  align-self: stretch;
  gap: 8px;

  .draft-icon {
    width: 20px;
    height: 20px;
    font-size: 20px;
  }

  .draft-mode-label {
    font-family: 'Gilroy Bold', Verdana, sans-serif;
    font-weight: 700;
    font-size: 16px;
    line-height: 20px;
    letter-spacing: 0;
    margin-bottom: 0;
    text-decoration: none;
    text-transform: none;
  }
}

.admin-settings-container {
  display: flex;
  align-items: center;
  align-self: stretch;
  gap: 8px;

  .admin-settings-icon {
    width: 20px;
    height: 20px;
    font-size: 20px;
  }

  .admin-settings-label {
    font-family: 'Gilroy Bold', Verdana, sans-serif;
    font-weight: 700;
    font-size: 16px;
    line-height: 20px;
    letter-spacing: 0;
    margin-bottom: 0;
    text-decoration: none;
    text-transform: none;
  }
}
</style>
